import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { GIFT_OPTIONS_STEP, REVIEW_STEP } from 'constants/checkoutFlow';

import { fetchCheckoutData, updateCheckoutForTargetStep } from 'actions/checkout/purchase';
import {
  fetchGiftOptions,
  setGiftMessage,
  setUseGiftBag,
  setUseGiftReceipt
} from 'actions/checkout/giftOptions';
import marketplace from 'cfg/marketplace.json';
import ContentBox from 'components/checkout/caap/ContentBox';
import GiftOption from 'components/checkout/caap/giftOptions/GiftOption';
import ModalOverlay from 'components/checkout/caap/ModalOverlay';
import SmallScreenOnly from 'components/checkout/caap/SmallScreenOnly';
import { getPathFromStep } from 'helpers/CheckoutFlowControl';
import css from 'styles/containers/checkout/giftOptionsSection.scss';

const { checkout: { allowGiftOptions } } = marketplace;

export class GiftOptionsSection extends Component {
  state = {
    hasFormError: false
  };

  componentDidMount() {
    const { checkoutData, fetchGiftOptions, updateCheckoutForTargetStep } = this.props;
    const { giftOptions: { lineItemId }, purchaseData: { targetStep } } = checkoutData;
    const isGiftOptionsStep = targetStep === GIFT_OPTIONS_STEP;
    fetchGiftOptions();
    if (isGiftOptionsStep && !lineItemId) {
      updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
    }
  }

  componentDidUpdate(prevProps) {
    const { checkoutData: { giftOptions: { lineItemId } } } = this.props;
    const { checkoutData: { giftOptions: { lineItemId: prevLineItemId } } } = prevProps;
    const { hasFormError } = this.state;

    if (prevLineItemId !== lineItemId && hasFormError) {
      this.setState({ hasFormError: false });
    }
  }

  onCancelClick = e => {
    e.preventDefault();
    this.conditionallyRemoveFormError();
    this.onCloseGiftOptionsClick(e);
  }

  onCloseGiftOptionsClick = e => {
    const { updateCheckoutForTargetStep } = this.props;
    e.preventDefault();
    this.conditionallyRemoveFormError();
    updateCheckoutForTargetStep({ targetStep: REVIEW_STEP, newUrl: getPathFromStep(REVIEW_STEP) });
  }

  onGiftMessageChange = event => {
    const { setGiftMessage } = this.props;
    const giftMessage = event.target.value;
    setGiftMessage(giftMessage);
  }

  onSaveGiftOptionsClick = e => {
    e.preventDefault();
    const { fetchCheckoutData, checkoutData } = this.props;
    const {
      giftOptions: {
        giftOptions: { giftOptions = {} },
        isLoading,
        lineItemId,
        giftMessage,
        useGiftBag,
        useGiftReceipt
      },
      purchaseData: {
        isFetchingCheckoutData,
        productsByLineItem
      }
    } = checkoutData;

    if (isFetchingCheckoutData || isLoading) {
      return;
    }

    if (useGiftBag && !giftMessage.length) {
      this.setState({ hasFormError: true });
      return;
    }

    const product = productsByLineItem[lineItemId];
    const { quantity, hasGiftReceipt: savedGiftReceipt } = product;
    const giftOption = giftOptions[lineItemId];
    const { giftWrap: [ { giftWrapId, unitPriceOverridden } ] } = giftOption;

    const options = {
      unitPriceOverridden,
      lineItemId,
      giftMessage,
      quantity,
      removeGiftOptions: false,
      giftReceipt: useGiftReceipt
    };

    if (useGiftBag) {
      options.giftWrapId = giftWrapId;
    }

    if (!useGiftReceipt) {
      if (!savedGiftReceipt) {
        // item didn't already have a gift receipt, no reason to call configure to remove it
        this.onCloseGiftOptionsClick(e);
        return;
      }
      options.removeGiftOptions = true;
    }

    this.conditionallyRemoveFormError();
    fetchCheckoutData({ advanceOnSuccess: true, showFullPageSpinner: false, giftOptions: [ options ] });
  }

  onUseGiftBagClick = () => {
    const { setUseGiftBag, setUseGiftReceipt, checkoutData } = this.props;
    const { giftOptions: { useGiftBag, useGiftReceipt } } = checkoutData;
    const newVal = !useGiftBag;
    setUseGiftBag(newVal);

    if (newVal) {
      // must have a gift receipt
      if (!useGiftReceipt) {
        setUseGiftReceipt(true);
      }
    }
  }

  onUseGiftReceiptClick = () => {
    const { setUseGiftBag, setUseGiftReceipt, checkoutData } = this.props;
    const { giftOptions: { useGiftReceipt } } = checkoutData;
    const newVal = !useGiftReceipt;
    setUseGiftReceipt(newVal);
    this.conditionallyRemoveFormError();

    if (!newVal) {
      // can't have gift wrap if no gift receipt
      setUseGiftBag(false);
    }
  }

  conditionallyRemoveFormError = () => {
    const { hasFormError } = this.state;
    if (hasFormError) {
      this.setState({ hasFormError: false });
    }
  }

  render() {
    const {
      giftOptions: {
        giftOptions: { giftOptions = {} },
        charMessage,
        charMessageWarning,
        giftMessage,
        isLoaded,
        isLoading,
        lineItemId,
        lineMessage,
        lineMessageWarning,
        useGiftBag,
        useGiftReceipt
      },
      purchaseData: {
        data: { eligibleForGiftWrap },
        isFetchingCheckoutData,
        targetStep,
        productsByLineItem
      }
    } = this.props.checkoutData;

    const { hasFormError } = this.state;
    const { testId } = this.context;
    const isGiftOptionsStep = targetStep === GIFT_OPTIONS_STEP;
    const onCloseModalOverlayClick = this.onCloseGiftOptionsClick;

    if (!isGiftOptionsStep) {
      return null;
    }

    if (!eligibleForGiftWrap || !allowGiftOptions || !isLoaded || !lineItemId || !productsByLineItem) {
      return null;
    }

    const product = productsByLineItem[lineItemId];
    const giftOption = giftOptions[lineItemId];
    const { giftWrap: [ { imageUrl: giftWrapImageUrl, price } ] } = giftOption;

    if (isLoading) {
      return (
        <ModalOverlay onCloseModalOverlayClick={onCloseModalOverlayClick} showAsModal={true}>
          <div className={css.modalTitleContainer}>
            Choose Gift Options
            <button
              type="button"
              className={css.closeButton}
              data-test-id={testId('closeModal')}
              onClick={onCloseModalOverlayClick}
              onKeyPress={onCloseModalOverlayClick}
              role="link"
              tabIndex="0"
              title="Close Modal"></button>
          </div>
          <ContentBox isSolo={true} isNonRadio={true}>Loading gift options...</ContentBox>
        </ModalOverlay>
      );
    }

    return (
      <ModalOverlay onCloseModalOverlayClick={onCloseModalOverlayClick} showAsModal={true}>
        <div className={css.modalTitleContainer}>
          Choose Gift Options
          <button
            type="button"
            className={css.closeButton}
            data-test-id={testId('closeModal')}
            onClick={onCloseModalOverlayClick}
            onKeyPress={onCloseModalOverlayClick}
            role="link"
            tabIndex="0"
            title="Close Modal"></button>
        </div>

        <SmallScreenOnly><h2>Choose Gift Options</h2></SmallScreenOnly>

        <form method="get" onSubmit={this.onSaveGiftOptionsClick}>
          <ContentBox isSolo={true} isNonRadio={true}>
            <GiftOption
              charMessage={charMessage}
              charMessageWarning={charMessageWarning}
              giftMessage={giftMessage}
              hasFormError={hasFormError}
              giftWrapImageUrl={giftWrapImageUrl}
              lineItemId={lineItemId}
              lineMessage={lineMessage}
              lineMessageWarning={lineMessageWarning}
              onGiftMessageChange={this.onGiftMessageChange}
              onUseGiftBagClick={this.onUseGiftBagClick}
              onUseGiftReceiptClick={this.onUseGiftReceiptClick}
              price={price}
              product={product}
              useGiftBag={useGiftBag}
              useGiftReceipt={useGiftReceipt}
            />
          </ContentBox>
        </form>

        <div className={css.continueButtonContainer}>
          <button
            className={css.checkoutPrimaryButton}
            data-test-id={testId('saveGiftOptions')}
            disabled={isFetchingCheckoutData || lineMessageWarning || charMessageWarning }
            onClick={this.onSaveGiftOptionsClick}
            type="submit">
            Save Gift Options
          </button>

          <button
            className={css.checkoutSecondaryButton}
            data-test-id={testId('cancelGiftOptions')}
            disabled={isFetchingCheckoutData}
            onClick={this.onCancelClick}
            type="submit">
            Cancel
          </button>
        </div>
      </ModalOverlay>
    );
  }
}

function mapStateToProps(state) {
  return {
    checkoutData: state.checkoutData
  };
}

GiftOptionsSection.contextTypes = {
  testId: PropTypes.func
};

export default connect(mapStateToProps, {
  fetchCheckoutData,
  fetchGiftOptions,
  setGiftMessage,
  setUseGiftBag,
  setUseGiftReceipt,
  updateCheckoutForTargetStep
})(GiftOptionsSection);
